import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Container } from '@mui/material';
import muiTheme from './theme/muiTheme';
import authService from './services/authService';
import gtmService from './services/gtmService';
import Header from './components/Header';
import Home from './components/Home';
import Store from './components/Store';
import WholesaleStore from './components/WholesaleStore';
import ProductDetail from './components/ProductDetail';
import MyOrders from './components/MyOrders';
import Login from './components/Login';
import Signup from './components/Signup';
import Profile from './components/Profile';
import Policy from './components/Policy';
import GiftShop from './components/GiftShop';
import Checkout from './components/Checkout';
import WholesaleCheckout from './components/WholesaleCheckout';
import ScrollToTop from './components/ScrollToTop';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineNotification from './components/OfflineNotification';
import UpdateNotification from './components/UpdateNotification';
import WhatsAppFloat from './components/WhatsAppFloat';
import './App.css';

// Component to handle wholesale user redirection from home page
const HomeWithRedirect = () => {
  const currentUser = authService.getCurrentUser();
  
  // If user is wholesale, redirect to wholesale store
  if (currentUser && currentUser.role === 'whole') {
    return <Navigate to="/wholesale" replace />;
  }
  
  // Otherwise show home page
  return <Home />;
};

// Component to protect wholesale store - only allow wholesale users
const ProtectedWholesaleStore = () => {
  const currentUser = authService.getCurrentUser();
  
  // If user is not logged in or not wholesale, redirect to home
  if (!currentUser || currentUser.role !== 'whole') {
    return <Navigate to="/" replace />;
  }
  
  // Otherwise show wholesale store
  return <WholesaleStore />;
};

// Component to protect wholesale checkout - only allow wholesale users
const ProtectedWholesaleCheckout = () => {
  const currentUser = authService.getCurrentUser();
  
  // If user is not logged in or not wholesale, redirect to home
  if (!currentUser || currentUser.role !== 'whole') {
    return <Navigate to="/" replace />;
  }
  
  // Otherwise show wholesale checkout
  return <WholesaleCheckout />;
};

// Component to track page views with GTM - must be inside Router context
const GTMPageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    gtmService.pageView(location.pathname + location.search);
  }, [location]);
  
  return null; // This component doesn't render anything
};

function App() {
  // Initialize Meta Pixel with optimized delayed loading for better performance
  useEffect(() => {
    const loadMetaPixelDelayed = () => {
      // Only load if not already loaded
      if (window.fbq) return;
      
      // Load Meta Pixel script with performance optimizations
      const initMetaPixel = (f,b,e,v,n,t,s) => {
        if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;t.defer=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s);
        
        // Add error handling
        t.onerror = () => console.warn('Meta Pixel failed to load');
      };
      
      // Initialize the script
      initMetaPixel(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      
      // Initialize with your Pixel ID
      window.fbq('init', '4907677319457935');
      window.fbq('track', 'PageView');
      
      // Add noscript fallback image (only if needed)
      if (!document.querySelector('noscript[data-meta-pixel]')) {
        const noscriptImg = document.createElement('noscript');
        noscriptImg.setAttribute('data-meta-pixel', 'true');
        noscriptImg.innerHTML = '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=4907677319457935&ev=PageView&noscript=1" />';
        document.head.appendChild(noscriptImg);
      }
    };

    // Enhanced loading strategy
    const schedulePixelLoad = () => {
      // Wait for critical resources to load first
      if (document.readyState === 'complete') {
        // Page fully loaded, wait a bit more for user interaction
        if (window.requestIdleCallback) {
          window.requestIdleCallback(loadMetaPixelDelayed, { timeout: 5000 });
        } else {
          setTimeout(loadMetaPixelDelayed, 3000); // Increased delay for better performance
        }
      } else {
        // Wait for page to finish loading
        window.addEventListener('load', () => {
          setTimeout(loadMetaPixelDelayed, 1000);
        }, { once: true });
      }
    };

    schedulePixelLoad();
  }, []);

  // Initialize Google Tag Manager
  useEffect(() => {
    gtmService.initialize('GTM-PZ857V5X');
  }, []);

  // You can add state management for cart items, user authentication, etc.
  const [cartItemCount] = React.useState(0); // Removed setCartItemCount for now

  // Handle search from header
  const handleSearch = (searchTerm) => {
    // You can implement global search logic here
    console.log('Search term:', searchTerm);
    // For now, we'll just log it, but you can integrate it with your store search
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <GTMPageTracker />
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header onSearch={handleSearch} />

          <Box component="main" sx={{ 
            marginTop: '64px',
            marginBottom: 0,
            minHeight: 'calc(100vh - 64px - 70px)',
            paddingBottom: '80px', // Add padding for mobile footer
            flexGrow: 1
          }}>
            <Routes>
              {/* Home Page - Redirect wholesale users to wholesale store */}
              <Route path="/" element={<HomeWithRedirect />} />
              
              {/* Store Page */}
              <Route path="/store" element={<Store />} />
              
              {/* Wholesale Store Page - Only for wholesale users */}
              <Route path="/wholesale" element={<ProtectedWholesaleStore />} />
              
              {/* Product Detail Page */}
              <Route path="/product/:id" element={<ProductDetail />} />
              
              {/* Checkout Page */}
              <Route path="/checkout" element={<Checkout />} />
              
              {/* Wholesale Checkout Page - Only for wholesale users */}
              <Route path="/wholesale/checkout" element={<ProtectedWholesaleCheckout />} />

              {/* Login Page */}
              <Route path="/login" element={<Login />} />

              {/* Signup Page */}
              <Route path="/register" element={<Signup />} />

              {/* Gift Shop Page - Restricted for wholesale users */}
              <Route path="/gift-shop" element={
                <RoleProtectedRoute deniedRoles={['whole']}>
                  <GiftShop />
                </RoleProtectedRoute>
              } />

              {/* Order History Page - placeholder */}
              <Route path="/order-history" element={
                <Container sx={{ 
                  padding: '50px', 
                  textAlign: 'center', 
                  fontSize: '18px' 
                }}>
                  تاريخ الطلبات (قيد التطوير)
                </Container>
              } />

              {/* My Orders Page */}
              <Route path="/my-orders" element={<MyOrders />} />

              {/* Profile Page */}
              <Route path="/profile" element={<Profile />} />

              {/* Policy Page */}
              <Route path="/policy" element={<Policy />} />
              
              {/* Redirect any unknown routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Box>

          <Box component="footer" sx={{ 
            textAlign: 'center',
            background: '#f5f5f5',
            padding: '16px 0',
            marginTop: 'auto'
          }}>
            متجر الصادق ©{new Date().getFullYear()} - جميع الحقوق محفوظة
          </Box>
        </Box>
        
        {/* PWA Install Prompt */}
        <PWAInstallPrompt />
        
        {/* Offline Notification */}
        <OfflineNotification />
        
        {/* Update Notification */}
        <UpdateNotification />
        
        {/* WhatsApp Float Button */}
        <WhatsAppFloat />
      </Router>
    </ThemeProvider>
  );
}

export default App;
